import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { SrIcon, type SrIconName } from "@/components/ui/SrIcon";

interface ColorToken {
  name: string;
  cssVar?: string;
  customProp?: string;
  hex: string;
  description?: string;
  textOn?: "light" | "dark";
}

const PRIMARY_COLORS: ColorToken[] = [
  { name: "Reveblå", customProp: "--sr-reveblaa", hex: "#031EFF", textOn: "dark", description: "Primær CTA, hero-detaljer" },
  { name: "Reverosa", customProp: "--sr-reverosa", hex: "#FF8888", textOn: "light", description: "Varm aksent, blob-overlap" },
  { name: "Revegrå", customProp: "--sr-revegraa", hex: "#DDE2E2", textOn: "light", description: "Standard side-bakgrunn" },
  { name: "Revemørk", customProp: "--sr-revemoerk", hex: "#081F3B", textOn: "dark", description: "Standard tekst, 1.5px-rammer" },
  { name: "Mørk", customProp: "--sr-moerk", hex: "#020A1F", textOn: "dark", description: "Sidebar / hero-flater" },
  { name: "Hvit", customProp: "--sr-hvit", hex: "#FFFFFF", textOn: "light", description: "Form-flater, kort" },
];

const SECONDARY_COLORS: ColorToken[] = [
  { name: "Lys blå", customProp: "--sr-lys-blaa", hex: "#50BDD8", textOn: "light" },
  { name: "Grønn", customProp: "--sr-groenn", hex: "#00AF68", textOn: "dark" },
  { name: "Sitron", customProp: "--sr-sitron", hex: "#BDD95A", textOn: "light" },
  { name: "Rosa", customProp: "--sr-rosa-hex", hex: "#F3A6D8", textOn: "light" },
  { name: "Red", customProp: "--sr-red-hex", hex: "#E93D20", textOn: "dark" },
  { name: "Blå", customProp: "--sr-blaa", hex: "#2340A8", textOn: "dark" },
];

const SHADCN_TOKENS: ColorToken[] = [
  { name: "background", cssVar: "--background", hex: "#FFFFFF" },
  { name: "foreground", cssVar: "--foreground", hex: "#081F3B" },
  { name: "primary", cssVar: "--primary", hex: "#031EFF" },
  { name: "secondary", cssVar: "--secondary", hex: "#DDE2E2" },
  { name: "muted", cssVar: "--muted", hex: "#F3F4F4" },
  { name: "destructive", cssVar: "--destructive", hex: "#E93D20" },
  { name: "success", cssVar: "--success", hex: "#00AF68" },
  { name: "sidebar", cssVar: "--sidebar-background", hex: "#020A1F" },
];

const SR_ICON_NAMES: SrIconName[] = [
  "fox", "dashboard", "folder", "file", "clock", "users", "receipt",
  "monitor", "settings", "logout", "chevron-left", "chevron-right",
  "menu", "eye", "eye-off", "x-circle", "check-circle", "check",
  "plus", "x", "filter", "money", "film", "alert", "arrow-right",
  "play", "pause", "bell", "info", "calendar", "edit", "trash",
  "download", "upload", "search", "more", "user", "asterisk",
  "scribble-underline", "scribble-circle", "empty-blob", "breathing-blob",
];

function ColorSwatch({ token }: { token: ColorToken }) {
  const ref = token.cssVar
    ? `hsl(var(${token.cssVar}))`
    : token.customProp
      ? `var(${token.customProp})`
      : token.hex;
  return (
    <div className="space-y-2">
      <div
        className="aspect-[3/2] border-[1.5px] border-[var(--sr-revemoerk)] flex items-end p-3"
        style={{ backgroundColor: ref }}
      >
        <span
          className={`text-[11px] font-mono ${token.textOn === "dark" ? "text-white/85" : "text-[var(--sr-revemoerk)]/80"}`}
        >
          {token.hex}
        </span>
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{token.name}</p>
        <p className="text-xs font-mono text-muted-foreground">
          {token.cssVar ?? token.customProp}
        </p>
        {token.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{token.description}</p>
        )}
      </div>
    </div>
  );
}

function Section({
  num,
  title,
  description,
  children,
}: {
  num: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="py-12 border-t-[1.5px] border-[var(--sr-revemoerk)]">
      <div className="mb-7">
        <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-muted-foreground mb-1.5">
          {num}
        </p>
        <h2 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] leading-[1.15]">
          {title}
        </h2>
        {description && (
          <p className="mt-2 text-[15px] leading-[1.6] text-muted-foreground max-w-[60ch]">
            {description}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

const STATUS_BADGES = [
  { key: "blue", className: "bg-[var(--sr-reveblaa)] text-white border-[var(--sr-reveblaa)]", label: "Tilbud godkjent" },
  { key: "green", className: "bg-[#D9F5E8] text-[#035C39] border-[var(--sr-revemoerk)]", label: "Oppstartsmøte gjennomført" },
  { key: "amber", className: "bg-[#FEF3C7] text-[#78350F] border-[var(--sr-revemoerk)]", label: "Delvis fakturert" },
  { key: "coral", className: "bg-[var(--sr-reverosa)] text-[var(--sr-revemoerk)] border-[var(--sr-revemoerk)]", label: "Mersalg venter" },
  { key: "red", className: "bg-[#FFE1DB] text-[#7F1D1D] border-[var(--sr-revemoerk)]", label: "Avvist" },
  { key: "outline", className: "bg-white text-[var(--sr-revemoerk)] border-[var(--sr-revemoerk)]", label: "Ikke planlagt" },
];

export default function StyleGuidePage() {
  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-12">
      {/* Masthead */}
      <header className="pb-8 border-b-[1.5px] border-[var(--sr-revemoerk)] flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <p className="text-[13px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
            Sølvrev · Designsystem
          </p>
          <h1 className="mt-2 font-display font-semibold text-[clamp(40px,5vw,72px)] leading-[1.04] tracking-[-0.01em]">
            Revehiet
            <br />
            Styleguide
          </h1>
        </div>
        <p className="text-[14px] leading-[1.6] text-muted-foreground max-w-[36ch] md:text-right">
          Levende referanse for tokens, ikoner og komponenter. Endringer i{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">src/index.css</code>
          {" "}eller{" "}
          <code className="text-xs bg-muted px-1 py-0.5 rounded">tailwind.config.ts</code>
          {" "}reflekteres umiddelbart her.
        </p>
      </header>

      {/* 01 Primærfarger */}
      <Section
        num="01 / Farger"
        title="Primærpalett"
        description="Seks kjernefarger. Resten av paletten er sekundær og brukes sparsomt."
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PRIMARY_COLORS.map((c) => (
            <ColorSwatch key={c.hex} token={c} />
          ))}
        </div>
      </Section>

      {/* 02 Sekundær */}
      <Section
        num="02 / Farger"
        title="Sekundærpalett"
        description="Status-badges, charts, og enkelte aksenter. Kombiner aldri mer enn to ut over Reveblå/Revemørk på én flate."
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {SECONDARY_COLORS.map((c) => (
            <ColorSwatch key={c.hex} token={c} />
          ))}
        </div>
      </Section>

      {/* 03 Shadcn-tokens */}
      <Section
        num="03 / Tokens"
        title="Shadcn-variabler"
        description="HSL-bridge mellom brand-paletten og shadcn/ui. Komponenter som Button, Input, Card leser disse."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {SHADCN_TOKENS.map((c) => (
            <ColorSwatch key={c.cssVar} token={c} />
          ))}
        </div>
      </Section>

      {/* 04 Typografi */}
      <Section
        num="04 / Typografi"
        title="Hierarki"
        description="Tre skriftfamilier. Clash Display kun for h1/h2. Clash Grotesk for alt brød. Thrash Punks aldri i funksjonell UI — kampanjer og eksterne flater."
      >
        <div className="space-y-8">
          <div className="space-y-3">
            <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Clash Display — semibold (600)
            </p>
            <p className="font-display font-semibold text-[clamp(40px,5vw,72px)] leading-[1.04] tracking-[-0.01em]">
              Tid, tilbud og tall.
            </p>
            <p className="font-display font-semibold text-[clamp(28px,3.2vw,44px)] leading-[1.2] tracking-[-0.005em]">
              Måltall, sidetittel
            </p>
          </div>

          <div className="space-y-3 border-t pt-6">
            <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Clash Grotesk — body
            </p>
            <p className="text-[18px] leading-[1.6] max-w-[60ch]">
              Lead/ingress: en lengre setning som viser linje­avstand og tekstvekt 400 i ingress-størrelse.
            </p>
            <p className="text-[16px] leading-[1.6] max-w-[60ch]">
              Brødtekst: standard avsnitt for forklaringer og lengre tekstpartier.
            </p>
            <p className="text-[13px] leading-[1.6] text-muted-foreground">
              Liten tekst: hjelpe­tekst og meta-info.
            </p>
            <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Micro-label: brukes som eyebrow over titler og i KPI-card.
            </p>
          </div>

          <div className="space-y-3 border-t pt-6">
            <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Thrash Punks — kampanjer (ikke i funksjonell UI)
            </p>
            <p className="font-display text-3xl">ThrashPunks Regular</p>
            <p className="font-punk-alt text-2xl">ThrashPunks Alt</p>
            <p className="font-punk-underline text-3xl">underlines aksent</p>
          </div>
        </div>
      </Section>

      {/* 05 Ikoner */}
      <Section
        num="05 / Ikoner"
        title="SrIcon-spriten"
        description={`42 brand-spesifikke SVG-symboler. Bruk via <SrIcon name="..." />. Ukjente navn faller tilbake til Lucide automatisk.`}
      >
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-[2px] bg-[var(--sr-revemoerk)] border-[1.5px] border-[var(--sr-revemoerk)]">
          {SR_ICON_NAMES.map((name) => (
            <div
              key={name}
              className="bg-white aspect-square flex flex-col items-center justify-center gap-2 px-3 py-4"
            >
              <span className="text-[var(--sr-revemoerk)]">
                <SrIcon name={name} size={28} />
              </span>
              <span className="text-[11px] font-mono text-muted-foreground text-center break-all">
                {name}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Farge arves via <code className="bg-muted px-1 py-0.5 rounded">currentColor</code> — wrap i en spann med <code className="bg-muted px-1 py-0.5 rounded">text-[var(--sr-reveblaa)]</code> for tinting.
        </p>
      </Section>

      {/* 06 Buttons */}
      <Section
        num="06 / Komponenter"
        title="Buttons"
        description="Primær = reveblå med arrow-right. Sekundær = hvit med 1.5px navy-ramme."
      >
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            <Button>
              <SrIcon name="plus" size={14} className="mr-1" />
              Primær
            </Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm">Small</Button>
            <Button>Default</Button>
            <Button size="lg">Large</Button>
            <Button size="icon"><SrIcon name="settings" size={16} /></Button>
          </div>
        </div>
      </Section>

      {/* 07 Status badges */}
      <Section
        num="07 / Komponenter"
        title="Status-badges"
        description="Pille-formet, 1.5px ramme, 12.5px tekst. Brukes i prosjekt-stepper, fakturastatus, og sidebar-badges."
      >
        <div className="flex flex-wrap gap-2.5">
          {STATUS_BADGES.map((b) => (
            <span
              key={b.key}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-[1.5px] text-[12.5px] font-medium ${b.className}`}
            >
              <span className="w-2 h-2 rounded-full bg-current opacity-80" />
              {b.label}
            </span>
          ))}
        </div>
      </Section>

      {/* 08 Inputs */}
      <Section
        num="08 / Komponenter"
        title="Inputs"
        description="Felter har 1.5px navy-ramme og fokus-ring i reveblå."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="sg-text">Tekstfelt</Label>
            <Input id="sg-text" placeholder="Skriv her..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sg-num">Nummerfelt</Label>
            <Input id="sg-num" type="number" placeholder="0" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sg-disabled">Disabled</Label>
            <Input id="sg-disabled" disabled value="Låst verdi" />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="sg-ta">Textarea</Label>
            <Textarea id="sg-ta" placeholder="Flere linjer tekst..." rows={3} />
          </div>
        </div>
      </Section>

      {/* 09 Cards */}
      <Section
        num="09 / Komponenter"
        title="Kort"
        description="Hovedstil: hvit bakgrunn + 1.5px navy-ramme. KPI-card støtter blå accent-variant."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Standard KPI */}
          <div className="border-[1.5px] border-[var(--sr-revemoerk)] bg-white p-5 pt-5 pb-[22px]">
            <div className="flex items-center gap-2 text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">
              <SrIcon name="money" size={14} />
              Fakturert
            </div>
            <div className="mt-3 font-display font-semibold text-[40px] leading-[1.05] tracking-[-0.02em] tabular-nums">
              2,84M
            </div>
            <div className="mt-1.5 text-[12.5px] text-success font-medium inline-flex items-center gap-1">
              <SrIcon name="arrow-right" size={12} />
              +12,4 % vs mars
            </div>
          </div>

          {/* Accent KPI */}
          <div className="relative border-[1.5px] border-[var(--sr-reveblaa)] bg-[var(--sr-reveblaa)] text-white p-5 pt-5 pb-[22px]">
            <div className="flex items-center gap-2 text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-80">
              <SrIcon name="clock" size={14} />
              Fakturerbare timer
            </div>
            <div className="mt-3 font-display font-semibold text-[40px] leading-[1.05] tracking-[-0.02em] tabular-nums">
              1 248
            </div>
            <div className="mt-1.5 text-[12.5px] opacity-90 inline-flex items-center gap-1">
              <SrIcon name="arrow-right" size={12} />
              94,3 % grad
            </div>
            <div className="absolute top-3 right-3 text-[var(--sr-reverosa)]">
              <SrIcon name="scribble-circle" size={22} />
            </div>
          </div>

          {/* Standard small */}
          <div className="border-[1.5px] border-[var(--sr-revemoerk)] bg-white p-5 pt-5 pb-[22px]">
            <div className="flex items-center gap-2 text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">
              <SrIcon name="film" size={14} />
              Aktive prosjekter
            </div>
            <div className="mt-3 font-display font-semibold text-[40px] leading-[1.05] tracking-[-0.02em] tabular-nums">
              17
            </div>
            <div className="mt-1.5 text-[12.5px] text-destructive font-medium inline-flex items-center gap-1">
              <SrIcon name="arrow-right" size={12} />
              −2 vs mars
            </div>
          </div>

          {/* Shadcn Card variant */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Shadcn Card</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Beholdes for komponenter som dropdown, dialog osv. som krever shadcn-baseline.
              </p>
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* 10 Frames */}
      <Section
        num="10 / Tokens"
        title="Rammer og hjørner"
        description="1.5px navy er hoved-rammen. Border-radius brukes minimalt — square-ish."
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "border-1.5", className: "border-[1.5px] border-[var(--sr-revemoerk)]" },
            { label: "rounded-md", className: "border-[1.5px] border-[var(--sr-revemoerk)] rounded-md" },
            { label: "rounded-lg", className: "border-[1.5px] border-[var(--sr-revemoerk)] rounded-lg" },
            { label: "rounded-pill", className: "border-[1.5px] border-[var(--sr-revemoerk)] rounded-pill" },
          ].map((t) => (
            <div key={t.label} className="space-y-2">
              <div className={`h-16 bg-white ${t.className}`} />
              <p className="text-xs font-mono text-muted-foreground">{t.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-10 border-t-[1.5px] border-[var(--sr-revemoerk)] mt-8">
        <p className="text-sm text-muted-foreground">
          Tokens redigeres i{" "}
          <code className="bg-muted px-1 py-0.5 rounded">src/index.css</code> +{" "}
          <code className="bg-muted px-1 py-0.5 rounded">tailwind.config.ts</code>. Sprite-ikoner i{" "}
          <code className="bg-muted px-1 py-0.5 rounded">src/assets/icons/sr-icons.svg</code>. Ny ikon? Legg til <code className="bg-muted px-1 py-0.5 rounded">&lt;symbol id="sr-..."&gt;</code> + utvid <code className="bg-muted px-1 py-0.5 rounded">SPRITE_NAMES</code> i SrIcon.
        </p>
      </footer>
    </div>
  );
}
